// @ts-nocheck
/**
 * Algolia Custom Hooks
 * Shared utilities and autocomplete hook — loaded on every page.
 * PLP-specific code (card update, add-to-cart, web component) lives in algolia-plp-hooks.js.
 */

// =================================================================
// TRANSLATIONS
// =================================================================
//
// Translations come from different sources depending on who renders the content:
//
//  Rendered by                 | Source
//  ----------------------------|------------------------------------------------------
//  Our custom hooks code       | window.themeStrings        (Shopify locale files)
//  Data value mappings         | window.themeSettings       (Shopify locale files)
//  Algolia's own widgets       | algoliaShopify.translations (Translate & Adapt app)
//  Algolia facets / sorts      | algoliaShopify.translations (Translate & Adapt app)
//  Per-resource localized data | hit.meta.translation       (Algolia index)
//
// Details:
//
// 1. window.themeStrings  (layout/theme.liquid → locales/en.default.json, locales/fr.json)
//    Rendered server-side via `| t` into a flat key→string map.
//    Used for UI strings we render ourselves: Add to cart, Sold out, Taxes Incl.,
//    New, Price Drop, Load more, Filters, Sort, No products found, THC/CBD labels, etc.
//    The T.get() helper reads from this object.
//
// 2. window.themeSettings.plantTypes  (layout/theme.liquid → locales/.../products.product.plant_types)
//    Rendered server-side via `| t` into a raw value → translated label map.
//    Keys: products.product.plant_types.{indica_dominant|sativa_dominant|hybrid|blend}
//
// 3. algoliaShopify.translations  (populated by Algolia via Shopify's Translate & Adapt app)
//    Algolia reads from store metafields managed by Translate & Adapt:
//      - algolia_search.translations       → UI strings for Algolia's own widgets
//      - algolia_search.instantsearch_facets       → facet titles
//      - algolia_search.instantsearch_sort_orders  → sort labels
//
// 4. hit.meta.translation  (Algolia index — synced from translation metafields)
//    Per-resource translated fields stored in the Algolia hit object.
//      - hit.meta.translation['fr-CA_title']    → FR product title
//      - hit.meta.translation['fr-CA_vendor']   → FR product vendor
//      - hit.meta.translation['fr-CA'|'en-CA']  → localized page/article handle
//      - hit.meta.translation.title_translation → FR page/article title
//
// =================================================================

// T is defined in snippets/algolia-t.liquid and injected inline before this script loads.

// =================================================================
// CONSTANTS & STATE
// =================================================================

/** Coming-soon placeholder — shown when a product has no image in the Algolia index. */
const COMING_SOON_URL = window.themeSettings?.comingSoonImageUrl || '';
function hitImageUrl(hit) {
  const url = algoliaShopify.helpers.mediumImage(hit);
  return (COMING_SOON_URL && url.includes('no-image-compact.gif')) ? COMING_SOON_URL : url;
}

function isAgeGateRestrictedSearch() {
  return Boolean(
    window.themeSettings?.searchAgeGateRestricted &&
    window.themeSettings?.ageGateRestrictedExperience,
  );
}

function ageGateRestrictedFilterForIndex(indexName) {
  if (!isAgeGateRestrictedSearch() || !indexName || indexName.includes('query_suggestion')) return '';
  if (indexName.includes('collections') || indexName.includes('pages')) return 'meta.auth.no_agegate:true';
  if (indexName.includes('articles') || indexName.includes('products')) return 'tags:no_age-gate';
  return '';
}

function appendAlgoliaFilter(existingFilter, filterToAppend) {
  if (!filterToAppend) return existingFilter || '';
  if (!existingFilter) return filterToAppend;
  if (existingFilter.includes(filterToAppend)) return existingFilter;
  return `(${existingFilter}) AND (${filterToAppend})`;
}

function collectionFlyoutLink(url) {
  if (typeof url !== 'string' || !url) return url;

  try {
    const parsed = new URL(url, window.location.origin);
    if (!parsed.pathname.includes('/collections/')) return url;

    parsed.searchParams.set('show_in_store', 'true');
    const isAbsolute = /^[a-z][a-z\d+.-]*:\/\//i.test(url);
    return isAbsolute ? parsed.href : `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch (_) {
    return url;
  }
}

// TEMPORARY LEGACY-DUPLICATE FILTERS — both storefront locales use the same
// canonical records and French storefronts localize those records at render time.
// Remove these filters after the legacy French duplicates leave Algolia.
const TEMPORARY_EXCLUDE_FRENCH_TAG_FILTER = 'NOT tags:"language--fr"';

function isTemporaryEnglishStorefront() {
  const locale = window.Shopify?.locale || document.documentElement?.lang || 'en';
  return !String(locale).toLowerCase().startsWith('fr');
}

function temporaryEnglishSourceIsEnabled(sourceId) {
  return window.themeSettings?.temporaryEnglishFilters?.[sourceId] !== false;
}

function temporaryEnglishFilterForSource(sourceId) {
  if (!temporaryEnglishSourceIsEnabled(sourceId)) return '';
  return sourceId === 'products' || sourceId === 'articles'
    ? TEMPORARY_EXCLUDE_FRENCH_TAG_FILTER
    : '';
}

function temporaryCanonicalResourceHitIsEligible(hit) {
  const indexedLanguage = String(hit?.meta?.locale?.language || '').toLowerCase();
  if (indexedLanguage) return !indexedLanguage.startsWith('fr');
  return !String(hit?.handle || '').toLowerCase().endsWith('-fr');
}

function temporaryEnglishHitIsEligible(sourceId, hit) {
  if (!temporaryEnglishSourceIsEnabled(sourceId)) return true;

  if (sourceId === 'products' || sourceId === 'articles') {
    const tags = Array.isArray(hit?.tags)
      ? hit.tags
      : (Array.isArray(hit?._tags) ? hit._tags : []);
    return !tags.some((tag) => String(tag).toLowerCase() === 'language--fr');
  }

  if (sourceId === 'pages' || sourceId === 'collections') {
    return temporaryCanonicalResourceHitIsEligible(hit);
  }

  return true;
}

function temporaryFilterEnglishHits(sourceId, hits) {
  if (!Array.isArray(hits)) return [];
  if (!temporaryEnglishSourceIsEnabled(sourceId)) return hits;
  return hits.filter((hit) => temporaryEnglishHitIsEligible(sourceId, hit));
}

window.algoliaLanguageFilters = Object.freeze({
  isEnglishLocale: isTemporaryEnglishStorefront,
  isSourceEnabled: temporaryEnglishSourceIsEnabled,
  filterForSource: temporaryEnglishFilterForSource,
  filterHits: temporaryFilterEnglishHits,
});
// END TEMPORARY LEGACY-DUPLICATE FILTERS

function addRestrictedFilterToQueryObject(queryObject, filterToAppend) {
  if (!queryObject || typeof queryObject !== 'object' || !filterToAppend) return;

  if (queryObject.params && typeof queryObject.params === 'object') {
    queryObject.params.filters = appendAlgoliaFilter(queryObject.params.filters, filterToAppend);
    return;
  }

  if (queryObject.searchParameters && typeof queryObject.searchParameters === 'object') {
    queryObject.searchParameters.filters = appendAlgoliaFilter(queryObject.searchParameters.filters, filterToAppend);
    return;
  }

  // Fallback: autocomplete source queries carry search params under `params`; a top-level
  // `filters` would be ignored by the request. Create `params` so the filter is actually sent.
  queryObject.params = queryObject.params || {};
  queryObject.params.filters = appendAlgoliaFilter(queryObject.params.filters, filterToAppend);
}

function setAutocompleteHitsPerPage(obj) {
  const value = window.innerWidth < 768
    ? window.themeSettings?.autocompleteHitsMobile
    : window.themeSettings?.autocompleteHitsDesktop;
  if (typeof value !== 'number' || !isFinite(value)) return;

  if (obj.params && typeof obj.params === 'object') {
    obj.params.hitsPerPage = value;
    return;
  }
  if (obj.searchParameters && typeof obj.searchParameters === 'object') {
    obj.searchParameters.hitsPerPage = value;
    return;
  }
  // Fallback: create params so the value is actually sent in the request.
  obj.params = obj.params || {};
  obj.params.hitsPerPage = value;
}

// Cache for product data to avoid refetching
const algoliaProductCache = {};

// =================================================================
// DATA HELPERS
// =================================================================

/**
 * Fetch product variant data from our custom Liquid endpoint.
 * Returns { variants: [{ id, available, option1, price, compare_at_price, availability }] }.
 */
async function algoliaFetchProduct(handle) {
  const cacheKey = `${handle}__${window.Shopify.locale}`;
  if (algoliaProductCache[cacheKey]) return algoliaProductCache[cacheKey];
  const response = await fetch(
    `${window.Shopify.routes.root}products/${handle}?view=ocs-variants`,
  );
  if (!response.ok) throw new Error("Failed to fetch product");
  const product = await response.json();
  algoliaProductCache[cacheKey] = product;
  return product;
}

function isInStoreOnly(hit) {
  return hit.meta?.variant?.availability === "in-store";
}

function isNewProduct(hit) {
  if (isInStoreOnly(hit)) return false;
  const tags = hit.tags || hit._tags || [];
  return tags.some((tag) => tag.toLowerCase() === "status--new");
}

function hasPriceDrop(hit) {
  return !!(
    hit.compare_at_price &&
    hit.compare_at_price > 0 &&
    hit.compare_at_price > hit.price
  );
}

function isOntarioGrown(hit) {
  if (!window.themeSettings?.enableOntarioGrown) return false;
  if (!window.themeSettings?.ontarioGrownLogoUrl) return false;
  const val = hit.meta?.product?.ontario_grown ?? hit["meta.product.ontario_grown"];
  return val === true || val === "true";
}

/**
 * Get cannabinoid values from variant metafields only (meta.variant.*_per).
 * Unit and division depend on content_uom_type:
 *   g → ÷10, "%"  |  capsule → as-is, "mg/capsule"  |  strip → as-is, "mg/strip"  |  else → "mg"
 */
function getCannabinoidValuesWithFallback(hit) {
  const uomType = (
    hit.meta?.product?.content_uom_type ||
    hit["meta.product.content_uom_type"] ||
    ""
  ).toLowerCase();

  const isGrams = uomType === "g";

  const unit = isGrams ? "%" :
    uomType === "capsule" ? "mg/capsule" :
    uomType === "strip"   ? "mg/strip"   :
    "mg";

  const getRaw = (key) => {
    const raw = hit.meta?.variant?.[key] ?? hit[`meta.variant.${key}`] ?? null;
    if (raw == null) return null;
    const num = parseFloat(raw);
    if (isNaN(num)) return null;
    return isGrams ? num / 10 : num;
  };

  return {
    thcMin: getRaw("thc_min_per"),
    thcMax: getRaw("thc_max_per"),
    cbdMin: getRaw("cbd_min_per"),
    cbdMax: getRaw("cbd_max_per"),
    unit,
    decimals: isGrams ? null : 2,
  };
}

function formatCannabinoidRange(min, max, unit = "%", decimals = null) {
  unit = (unit || "%").toLowerCase();
  const fmt = (v) => {
    if (v == null) return null;
    const n = parseFloat(v);
    if (isNaN(n)) return null;
    const precision = decimals != null ? decimals : 2;
    const factor = 10 ** precision;
    const rounded = Math.round((n + Number.EPSILON) * factor) / factor;
    return decimals != null ? rounded.toFixed(decimals) : String(rounded);
  };
  const minStr = fmt(min);
  const maxStr = fmt(max);
  if (minStr == null && maxStr == null) return null;
  if (minStr == null) return `${maxStr}${unit}`;
  if (maxStr == null) return `${minStr}${unit}`;
  if (minStr === maxStr) return `${minStr}${unit}`;
  return `${minStr} - ${maxStr}${unit}`;
}

// =================================================================
// RENDER FUNCTIONS (autocomplete panel — lit-html only)
// =================================================================

function getOntarioGrownLogoUrl() {
  const isFr = document.documentElement.lang.startsWith("fr");
  return (isFr && window.themeSettings.ontarioGrownLogoUrlFr)
    ? window.themeSettings.ontarioGrownLogoUrlFr
    : window.themeSettings.ontarioGrownLogoUrl;
}

function renderCannabinoidInfo(hit, html) {
  const { thcMin, thcMax, cbdMin, cbdMax, unit, decimals } = getCannabinoidValuesWithFallback(hit);
  const thcRange = formatCannabinoidRange(thcMin, thcMax, unit, decimals);
  const cbdRange = formatCannabinoidRange(cbdMin, cbdMax, unit, decimals);
  if (!thcRange && !cbdRange) return "";
  return html`
    <div class="ais-hit--cannabinoids">
      ${thcRange && html`<span class="ais-hit--thc">${T.thcLabel()}: ${thcRange}</span>`}
      ${cbdRange && html`<span class="ais-hit--cbd">${T.cbdLabel()}: ${cbdRange}</span>`}
    </div>
  `;
}

function renderBadges(hit, html) {
  const showNew = isNewProduct(hit);
  const showPriceDrop = hasPriceDrop(hit);
  if (!showNew && !showPriceDrop) return "";
  return html`
    <div class="ais-hit--badges" role="list" aria-label="${T.productBadgesLabel()}">
      ${showNew ? html`<span class="ais-hit--badge ais-hit--badge-new" role="listitem">${T.newLabel()}</span>` : ""}
      ${showPriceDrop ? html`<span class="ais-hit--badge ais-hit--badge-pricedrop" role="listitem">${T.discountLabel()}</span>` : ""}
    </div>
  `;
}

function renderOntarioGrownBadge(hit, html) {
  if (!isOntarioGrown(hit)) return "";
  const logoUrl = getOntarioGrownLogoUrl();
  return html`
    <div class="ais-hit--ontario-grown">
      <img src="${logoUrl}" alt="${T.ontarioGrownAlt()}" title="${T.ontarioGrownTooltip()}" class="ais-hit--ontario-grown-img" />
    </div>
  `;
}

function algoliaContentLocaleKey() {
  const locale = window.Shopify?.locale || document.documentElement?.lang || 'en';
  return String(locale).toLowerCase().startsWith('fr') ? 'fr-CA' : 'en-CA';
}

function algoliaLocalizedContentHandle(hit) {
  const localeKey = algoliaContentLocaleKey();
  return hit?.meta?.translation?.[localeKey] || hit?.handle || '';
}

function algoliaContentTitleFromHandle(handle) {
  const title = String(handle || '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return title ? title.charAt(0).toLocaleUpperCase('fr-CA') + title.slice(1) : '';
}

function algoliaLocalizedContentTitle(hit) {
  if (algoliaContentLocaleKey() !== 'fr-CA') return hit?.title || '';
  return hit?.meta?.translation?.title_translation ||
    algoliaContentTitleFromHandle(hit?.meta?.translation?.['fr-CA']) ||
    hit?.title ||
    '';
}

function algoliaLocalizedContentUrl(hit, sourceId) {
  const root = window.Shopify?.routes?.root || '/';
  const localeRoot = root.endsWith('/') ? root : `${root}/`;
  const handle = algoliaLocalizedContentHandle(hit);
  if (sourceId === 'pages') return `${localeRoot}pages/${handle}`;

  const blogHandle = hit?.blog?.handle || 'news';
  return `${localeRoot}blogs/${blogHandle}/${handle}`;
}

function autocompleteLocalizedContentTitle(item, components) {
  const isFR = algoliaContentLocaleKey() === 'fr-CA';
  const translatedTitle = isFR ? item?.meta?.translation?.title_translation : '';
  const localizedHandle = isFR ? item?.meta?.translation?.['fr-CA'] : '';
  const value = algoliaLocalizedContentTitle(item);
  if (!value) return { value: '', rendered: '' };

  if (translatedTitle) {
    const translatedHighlight = item?._highlightResult?.meta?.translation?.title_translation;
    return {
      value,
      rendered: translatedHighlight
        ? components.Highlight({ attribute: ['meta', 'translation', 'title_translation'], hit: item })
        : value,
    };
  }

  if (localizedHandle) return { value, rendered: value };

  return {
    value,
    rendered: item?._highlightResult?.title
      ? components.Highlight({ attribute: 'title', hit: item })
      : value,
  };
}

function renderAutocompleteContentTemplate(templateOptions, sourceId) {
  const { html, item, components } = templateOptions;
  const titleField = autocompleteLocalizedContentTitle(item, components);
  const itemUrl = algoliaLocalizedContentUrl(item, sourceId);

  return html`
    <a href="${itemUrl}" class="aa-ItemLink">
      <div class="aa-ItemContent">
        <div class="aa-ItemContentBody">
          <div class="aa-ItemContentTitle" title="${titleField.value}">${titleField.rendered}</div>
        </div>
      </div>
    </a>
  `;
}

function autocompleteLocalizedField(item, components, field, isFR) {
  const translationKey = `fr-CA_${field}`;
  const translatedValue = isFR ? item?.meta?.translation?.[translationKey] : '';
  const fallbackValue = item?.[field] || '';
  const value = translatedValue || fallbackValue;
  if (!value) return { value: '', rendered: '' };

  if (translatedValue) {
    const translatedHighlight = item?._highlightResult?.meta?.translation?.[translationKey];
    return {
      value,
      rendered: translatedHighlight
        ? components.Highlight({ attribute: ['meta', 'translation', translationKey], hit: item })
        : value,
    };
  }

  return {
    value,
    rendered: item?._highlightResult?.[field]
      ? components.Highlight({ attribute: field, hit: item })
      : value,
  };
}

// =================================================================
// AUTOCOMPLETE HOOK
// =================================================================

function registerAutocompleteHook() {

  function withTemporaryEnglishHitFiltering(config, sourceId) {
    const shouldFilterHits = temporaryEnglishSourceIsEnabled(sourceId) &&
      (sourceId === 'collections' || sourceId === 'pages');
    const searchClient = config?.searchClient;
    if (!shouldFilterHits || !searchClient || typeof searchClient.search !== 'function') {
      return config;
    }

    const wrappedClient = Object.create(searchClient);
    wrappedClient.search = async function (requests) {
      const response = await searchClient.search(requests);
      if (!response || !Array.isArray(response.results)) return response;

      return {
        ...response,
        results: response.results.map((result) => ({
          ...result,
          hits: temporaryFilterEnglishHits(sourceId, result.hits),
        })),
      };
    };

    return { ...config, searchClient: wrappedClient };
  }

  // Returns the effective index prefix, normalised to always end with "_".
  // Theme setting takes priority over the app config value; if the theme setting
  // is blank the app value is used as-is (already has trailing "_").
  function resolveIndexPrefix() {
    const themeP = window.themeSettings?.algoliaIndexPrefix;
    const appP = window.algoliaShopify?.config?.index_prefix || "";
    const raw = (themeP && themeP.trim()) ? themeP.trim() : appP;
    return raw && !raw.endsWith("_") ? raw + "_" : raw;
  }

  // Resolve the search-tuned replica index for the current locale.
  // The search replica is used for autocomplete and /search results; collection PLPs
  // continue to use the primary (browse-ranked) index.
  function resolveSearchProductsIndex() {
    const isFR = window.Shopify?.locale?.startsWith('fr');
    const prefix = resolveIndexPrefix();
    const configuredSuffix = isFR ? window.themeSettings?.searchIndexes?.fr : window.themeSettings?.searchIndexes?.en;
    const suffix = configuredSuffix || (isFR ? 'search_products_canada_fr' : 'search_products');
    return `${prefix}${suffix}`;
  }

  // Rewrite any indexName in the autocomplete config that matches the primary products
  // index to the search replica. A defensive walker is used because the exact key path
  // varies by app version (plugins[i].options.indexName, sources[i].queries[j].indexName, etc.).
  algoliaShopify.hooks.registerHook('beforeAutocompleteConfigurationOptions', (config, context = {}) => {
    const sourceId = context.sourceId || '';
    const themePrefix = resolveIndexPrefix();
    // Keep a direct read of the app prefix so we can detect a mismatch.
    const appPrefixRaw = window.algoliaShopify?.config?.index_prefix || '';
    const appPrefix = appPrefixRaw && !appPrefixRaw.endsWith('_')
      ? appPrefixRaw + '_'
      : appPrefixRaw;
    // True when the theme has overridden the prefix to a different environment.
    const prefixDiffers = appPrefix !== themePrefix && appPrefix.length > 0;

    // Primary index names that should be redirected to the search replica.
    // These are built with themePrefix because by the time we compare, any
    // leading appPrefix will already have been rewritten to themePrefix (Step 1).
    const primaryEN = `${themePrefix}products`;
    const primaryFR = `${themePrefix}products_canada_fr`;
    const searchIndex = resolveSearchProductsIndex();
    let rewritten = 0;
    let sawProductsLikeIndex = false;

    function walkAndRewrite(obj, depth, visited) {
      if (depth > 6 || obj === null || typeof obj !== 'object') return;
      if (visited.has(obj)) return;
      visited.add(obj);
      const keys = Array.isArray(obj) ? obj.map((_, i) => i) : Object.keys(obj);
      keys.forEach((k) => {
        if (k === 'indexName') {
          // Step 1 — remap a mismatched environment prefix on every source
          // (query-suggestions, collections, pages, articles, products).
          // No-op when prefixes are equal so existing behaviour is preserved.
          if (prefixDiffers && typeof obj[k] === 'string' && obj[k].startsWith(appPrefix)) {
            obj[k] = themePrefix + obj[k].slice(appPrefix.length);
          }

          if (typeof obj[k] === 'string' && obj[k].includes('products') && !obj[k].includes('query_suggestion')) {
            sawProductsLikeIndex = true;
          }

          // Step 2 — swap the primary products index for the search replica.
          if (obj[k] === primaryEN || obj[k] === primaryFR) {
            obj[k] = searchIndex;
            rewritten++;
          }

          const restrictedFilter = ageGateRestrictedFilterForIndex(obj[k]);
          if (restrictedFilter) {
            addRestrictedFilterToQueryObject(obj, restrictedFilter);
          }
          const languageFilter = temporaryEnglishFilterForSource(sourceId);
          if (languageFilter) {
            addRestrictedFilterToQueryObject(obj, languageFilter);
          }
          if (typeof obj[k] === 'string' && obj[k].includes('products') && !obj[k].includes('query_suggestion')) {
            setAutocompleteHitsPerPage(obj);
          }
          return;
        }
        walkAndRewrite(obj[k], depth + 1, visited);
      });
    }

    walkAndRewrite(config, 0, new WeakSet());
    if (sawProductsLikeIndex && rewritten === 0) {
      console.warn('[Algolia] beforeAutocompleteConfigurationOptions: products-like indexName found but not rewritten — check primary index naming.');
    }
    return withTemporaryEnglishHitFiltering(config, sourceId);
  });

  // Customize autocomplete product card template.
  // templateOptions.item is the full Algolia hit — cannabinoid fields available directly.
  algoliaShopify.hooks.registerHook('beforeAutocompleteProductTemplate', (_defaultTemplate, templateOptions, distinct, itemLink, trackSearchAttribution) => {
    const { html, item, components } = templateOptions;

    const isFR = window.Shopify?.locale?.startsWith('fr');
    const titleField = autocompleteLocalizedField(item, components, 'title', isFR);
    const vendorField = autocompleteLocalizedField(item, components, 'vendor', isFR);
    const imageUrl = hitImageUrl(item);

    return html`
      <a href="${itemLink}" class="aa-ItemLink" data-ocs-origin-kind="search" data-ocs-item-list-name="Search" onClick="${(event) => {
        trackSearchAttribution(item);
        // Search-flyout GA4 event (assets/ocs-search-analytics.js). Optional-chained
        // and internally guarded, so the flyout behaves exactly as before if the
        // analytics module is absent or throws. title/vendor are passed in already
        // localized rather than re-derived from the hit.
        window.OCSSearchAnalytics?.publishFlyoutProduct(item, event.currentTarget, {
          title: titleField.value,
          vendor: vendorField.value,
        });
      }}">
        ${renderBadges(item, html)}
        <div class="ais-hit--info">
          <div class="ais-hit--link">
            <img class="ais-hit--picture" src="${imageUrl}" alt="${titleField.value}" loading="lazy" />
          </div>
          <div class="ais-hit--details">
            ${vendorField.value && html`<p class="ais-hit--vendor">${vendorField.rendered}</p>`}
            <p class="ais-hit--title" title="${titleField.value}">
              ${titleField.rendered}
            </p>
            ${renderCannabinoidInfo(item, html)}
          </div>
        </div>
      </a>
    `;
  });

  algoliaShopify.hooks.registerHook('beforeAutocompleteCollectionsTemplate', (_defaultTemplate, templateOptions, itemLink) => {
    const { html, item, components } = templateOptions;
    const collectionLink = collectionFlyoutLink(itemLink);

    return html`
      <a href="${collectionLink}" class="aa-ItemLink aa-ProductItem">
        <div class="aa-ItemWrapper">
          <div class="aa-ItemContent">
            <div class="aa-ItemIcon aa-ItemIcon--noBorder">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                />
              </svg>
            </div>
            <div class="aa-ItemContentBody">
              <div class="aa-ItemContentTitle">
                ${components.Highlight({ hit: item, attribute: 'title' })}
              </div>
            </div>
          </div>
        </div>
      </a>
    `;
  });

  algoliaShopify.hooks.registerHook('beforeAutocompletePagesTemplate', (_defaultTemplate, templateOptions) => (
    renderAutocompleteContentTemplate(templateOptions, 'pages')
  ));

  algoliaShopify.hooks.registerHook('beforeAutocompleteArticlesTemplate', (_defaultTemplate, templateOptions) => (
    renderAutocompleteContentTemplate(templateOptions, 'articles')
  ));

}

// =================================================================
// INITIALIZATION
// =================================================================

if (typeof algoliaShopify !== "undefined" && algoliaShopify.hooks) {
  registerAutocompleteHook();
} else {
  document.addEventListener("algolia.hooks.initialize", () => {
    registerAutocompleteHook();
  });
}

window.algoliaCustom = window.algoliaCustom || {};

// Rewrite the no-results header text to "No Results for 'query'" format.
(function rewriteNoResultsText() {
  function rewrite(el) {
    const query = document.querySelector('#algolia-search-bar .aa-Input')?.value?.trim() || '';
    el.textContent = query ? T.searchNoResultsFor(query) : T.noResultsFound();
  }

  new MutationObserver((mutations) => {
    mutations.forEach(({ addedNodes }) => {
      addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        const header = node.classList?.contains('aa-NoResultsHeader')
          ? node
          : node.querySelector?.('.aa-NoResultsHeader');
        if (header) rewrite(header);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
})();

// Move .aa-SourceFooter ("Show All Results" btn) after .aa-PanelSections.
// Keeping the footer outside the variable-height product grid lets desktop
// reserve a stable row for it; mobile already relies on this same ordering.
(function moveSourceFooter() {
  function move(footer) {
    const panelLayout = footer.closest('.aa-PanelLayout');
    if (!panelLayout) return;
    panelLayout.querySelectorAll('.aa-SourceFooter').forEach(el => {
      if (el !== footer) el.remove();
    });
    if (footer.parentElement !== panelLayout) panelLayout.appendChild(footer);
  }

  new MutationObserver((mutations) => {
    mutations.forEach(({ addedNodes }) => {
      addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        const footer = node.classList?.contains('aa-SourceFooter')
          ? node
          : node.querySelector?.('.aa-SourceFooter');
        if (footer) move(footer);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
})();

function searchResultLayout(productCount, collectionCount, articleCount, pageCount) {
  const contentCount = collectionCount + articleCount + pageCount;
  if (productCount > 0 && contentCount === 0) return 'products-only';
  if (productCount === 0 && contentCount > 0) return 'content-only';
  if (productCount > 0 && contentCount > 0) return 'mixed';
  return 'empty';
}

function searchProductGridLayout(resultLayout, productCount) {
  return resultLayout === 'products-only' && productCount >= 3 ? 'fill' : 'fixed';
}

function shouldShowAllResults(productCount, productTotal, collectionCount, articleCount) {
  const visibleResultCount = productCount + collectionCount + articleCount;
  if (visibleResultCount <= 1) return false;

  if (productTotal > 6) return true;
  return productCount <= 6 && (collectionCount > 0 || articleCount > 0);
}

// Algolia only supplies its footer for product overflow. Also expose the search
// destination when six or fewer products are accompanied by collection or
// article matches, so shoppers can continue to the complete mixed result set.
(function manageShowAllResults() {
  let syncQueued = false;

  function resultCount(panel, sourceId) {
    return panel.querySelectorAll(
      `[data-autocomplete-source-id="${sourceId}"] .aa-Item`
    ).length;
  }

  function searchResultsUrl() {
    const root = window.Shopify?.routes?.root || '/';
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
    const url = new URL(`${normalizedRoot}search`, window.location.origin);
    const query = document.querySelector('#algolia-search-bar .aa-Input, .aa-DetachedContainer .aa-Input')?.value?.trim();
    if (query) url.searchParams.set('q', query);
    return `${url.pathname}${url.search}`;
  }

  function createFooter(panelLayout) {
    const footer = document.createElement('div');
    footer.className = 'aa-SourceFooter';
    footer.dataset.ocsGeneratedShowAll = 'true';

    const inner = document.createElement('div');
    inner.className = 'aa-footer';

    const link = document.createElement('a');
    link.className = 'aa-SeeAllBtn';
    link.textContent = T.searchShowAllResults();
    inner.appendChild(link);
    footer.appendChild(inner);
    panelLayout.appendChild(footer);
    return link;
  }

  function syncPanel(panel) {
    const panelLayout = panel.querySelector('.aa-PanelLayout');
    if (!panelLayout) return;

    const productCount = resultCount(panel, 'products');
    const collectionCount = resultCount(panel, 'collections');
    const articleCount = resultCount(panel, 'articles');
    const pageCount = resultCount(panel, 'pages');
    panel.dataset.searchResultLayout = searchResultLayout(
      productCount,
      collectionCount,
      articleCount,
      pageCount
    );
    panel.dataset.productGridLayout = searchProductGridLayout(
      panel.dataset.searchResultLayout,
      productCount
    );

    let button = panel.querySelector('.aa-SeeAllBtn');
    const naturalProductTotal = Number(button?.dataset.ocsProductTotal);
    const productTotal = Number.isFinite(naturalProductTotal) && naturalProductTotal > 0
      ? naturalProductTotal
      : (button && !button.closest('[data-ocs-generated-show-all]') ? 7 : productCount);
    const show = shouldShowAllResults(productCount, productTotal, collectionCount, articleCount);

    if (!button && show) button = createFooter(panelLayout);
    if (!button) return;

    const footer = button.closest('.aa-SourceFooter');
    if (!show && footer?.dataset.ocsGeneratedShowAll === 'true') {
      footer.remove();
      return;
    }

    if (footer) footer.hidden = !show;
    button.hidden = !show;
    if (show) button.href = searchResultsUrl();
  }

  function queueSync() {
    if (syncQueued) return;
    syncQueued = true;
    requestAnimationFrame(() => {
      syncQueued = false;
      document.querySelectorAll('.aa-Panel').forEach(syncPanel);
    });
  }

  new MutationObserver(queueSync).observe(document.body, { childList: true, subtree: true });
  document.addEventListener('input', (event) => {
    if (event.target?.matches?.('.aa-Input')) queueSync();
  });
})();

// Rewrite the autocomplete source headers (Collections, Articles, Pages, Products)
// from Algolia's default English text to the locale-aware theme strings.
// Algolia renders each as .aa-SourceHeaderTitle: Collections/Articles/Pages live
// inside their respective [data-autocomplete-source-id="..."] containers, while
// Products sits in .aa-PanelSection--right without a matching source-id container
// (see the DOM notes below) — we have no hook for the source header text, so a
// MutationObserver is the correct approach here.
(function rewriteSourceHeaders() {
  const SOURCE_ID_LABELS = {
    collections: T.searchResourceCollections,
    articles: T.searchResourceArticles,
    pages: T.searchResourcePages,
  };

  function rewrite(node) {
    Object.keys(SOURCE_ID_LABELS).forEach((id) => {
      const container = node.matches?.(`[data-autocomplete-source-id="${id}"]`)
        ? node
        : node.querySelector?.(`[data-autocomplete-source-id="${id}"]`);
      if (!container) return;
      const title = container.querySelector('.aa-SourceHeaderTitle');
      if (title) title.textContent = SOURCE_ID_LABELS[id]();
    });

    // Products have no data-autocomplete-source-id container of their own —
    // they sit in .aa-PanelSection--right (see positionProductsOnMobile below).
    const rightPanel = node.matches?.('.aa-PanelSection--right')
      ? node
      : node.querySelector?.('.aa-PanelSection--right');
    if (!rightPanel) return;
    const productsTitle = rightPanel.querySelector('.aa-SourceHeaderTitle');
    if (productsTitle) productsTitle.textContent = T.searchResourceProducts();
  }

  new MutationObserver((mutations) => {
    mutations.forEach(({ addedNodes }) => {
      addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        rewrite(node);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
})();

// On mobile, position .aa-PanelSection--right (Products) at the top of .aa-PanelSection--left,
// or immediately after the query-suggestion pills source if one is present.
// On desktop the two-column layout handles ordering via CSS — guard at entry.
//
// Live DOM (mobile):
//   .aa-PanelSection--left
//     [data-autocomplete-source-id="collections"]
//     .aa-PanelSection--right (products — moved here by this observer)
//     [data-autocomplete-source-id="articles"]
//     [data-autocomplete-source-id="pages"]
//
// There are no query-suggestion pills on this store. If they are ever added,
// the SUGGESTIONS_IDS check below will catch them and products will sit after them.
(function positionProductsOnMobile() {
  // Known Algolia source-ids for query-suggestion / pills sources (case-insensitive substring match).
  const SUGGESTION_IDS = ['querysuggestion', 'suggestion', 'popularsearches', 'suggestedsearches', 'recentsearches', 'trending'];

  new MutationObserver(() => {
    // Desktop: no-op — two-column CSS layout handles ordering.
    if (window.innerWidth >= 768) return;

    const leftPanel = document.querySelector('.aa-PanelSection--left');
    if (!leftPanel) return;
    const rightPanel = document.querySelector('.aa-PanelSection--right');
    if (!rightPanel) return;

    // Look for a query-suggestions/pills source by checking data-autocomplete-source-id
    // against the known set. Match is case-insensitive substring.
    const allSources = leftPanel.querySelectorAll('[data-autocomplete-source-id]');
    let suggestionsSource = null;
    for (const src of allSources) {
      const id = (src.getAttribute('data-autocomplete-source-id') || '').toLowerCase();
      if (SUGGESTION_IDS.some((term) => id.includes(term))) {
        suggestionsSource = src;
        break;
      }
    }

    if (suggestionsSource) {
      // Pills present: products go immediately after them.
      // Idempotency: skip if already in the correct position.
      if (suggestionsSource.nextSibling === rightPanel) return;
      suggestionsSource.insertAdjacentElement('afterend', rightPanel);
    } else {
      // No pills: products go first.
      // Idempotency: skip if already the first child.
      if (leftPanel.firstChild === rightPanel) return;
      leftPanel.prepend(rightPanel);
    }
  }).observe(document.body, { childList: true, subtree: true });
})();

// Replace Algolia's "See all products (n)" button text with "Show All Results".
(function rewriteSeeAllBtn() {
  function rewrite(btn) {
    const countMatch = btn.textContent.match(/(\d[\d,]*)/);
    if (countMatch) btn.dataset.ocsProductTotal = countMatch[1].replace(/,/g, '');
    btn.textContent = T.searchShowAllResults();
  }
  new MutationObserver((mutations) => {
    mutations.forEach(({ addedNodes }) => {
      addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        const btn = node.classList?.contains('aa-SeeAllBtn')
          ? node
          : node.querySelector?.('.aa-SeeAllBtn');
        if (btn) rewrite(btn);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
})();

// Track #algolia-search-bar width and expose as --algolia-search-bar-width on body.
(function trackSearchBarWidth() {
  const container = document.getElementById('algolia-search-bar');
  if (!container) return;
  const update = () => {
    document.body.style.setProperty('--algolia-search-bar-width', `${container.offsetWidth}px`);
  };
  new ResizeObserver(update).observe(container);
  update();
})();

// Track the actual rendered height of the .header-section element and expose it
// as --header-section-height on body.
(function trackHeaderSectionHeight() {
  const headerSection = document.querySelector('.header-section');
  if (!headerSection) return;
  const update = () => {
    document.body.style.setProperty('--header-section-height', `${headerSection.offsetHeight}px`);
  };
  new ResizeObserver(update).observe(headerSection);
  update();
})();

// Gets the search bar's height as --algolia-search-bar-height so sticky sections below it can offset.
(function trackSearchBarSectionHeight() {
  const section = document.querySelector('.section-algolia-search-bar');
  if (!section) return;
  const update = () => {
    document.body.style.setProperty('--algolia-search-bar-height', `${section.offsetHeight}px`);
  };
  new ResizeObserver(update).observe(section);
  update();
})();

// Backdrop + scroll lock when the Algolia search panel is open.
(function lockScrollOnSearchPanel() {
  let _backdrop = null;
  let _scrollLockState = null;

  function lockBodyScroll() {
    if (_scrollLockState) return;

    const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    const computedPaddingInlineEnd = parseFloat(window.getComputedStyle(document.body).paddingInlineEnd) || 0;

    _scrollLockState = {
      overflow: document.body.style.overflow,
      paddingInlineEnd: document.body.style.paddingInlineEnd,
    };

    document.body.style.overflow = 'hidden';

    if (scrollbarWidth > 0) {
      document.body.style.paddingInlineEnd = `${computedPaddingInlineEnd + scrollbarWidth}px`;
    }
  }

  function unlockBodyScroll() {
    if (!_scrollLockState) return;

    document.body.style.overflow = _scrollLockState.overflow;
    document.body.style.paddingInlineEnd = _scrollLockState.paddingInlineEnd;
    _scrollLockState = null;
  }

  function showBackdrop() {
    if (_backdrop) return;
    _backdrop = document.createElement('div');
    _backdrop.className = 'search-panel-backdrop';
    _backdrop.addEventListener('click', () => {
      const input = document.querySelector('#algolia-search-bar .aa-Input');
      if (input) input.blur();
    });
    document.body.appendChild(_backdrop);
  }

  function hideBackdrop() {
    if (!_backdrop) return;
    const el = _backdrop;
    _backdrop = null;
    el.classList.add('is-closing');
    el.addEventListener('animationend', () => el.remove(), { once: true });
  }

  function observe(autocomplete) {
    new MutationObserver(() => {
      const isOpen = autocomplete.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        lockBodyScroll();
        showBackdrop();
      } else {
        unlockBodyScroll();
        hideBackdrop();
      }
    }).observe(autocomplete, { attributes: true, attributeFilter: ['aria-expanded'] });
  }

  const existing = document.querySelector('#algolia-search-bar .aa-Autocomplete');
  if (existing) { observe(existing); return; }

  new MutationObserver((_, mo) => {
    const el = document.querySelector('#algolia-search-bar .aa-Autocomplete');
    if (!el) return;
    mo.disconnect();
    observe(el);
  }).observe(document.body, { childList: true, subtree: true });
})();
